import type { LucideIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib/utils";
import type { TaxClosingPeriodItem } from "@/services/tax-closing-period";

import { MONTHS_PT } from "../constants";
import {
  PERIOD_STATUS_ICON,
  PERIOD_STATUS_LABELS,
  type TaxClosingPeriodStatus,
} from "../status";

interface StatusVisual {
  label: string;
  iconWrap: string;
  Icon: LucideIcon;
}

const STATUS_VISUAL: Record<TaxClosingPeriodStatus, StatusVisual> = {
  RELEASED: {
    label: PERIOD_STATUS_LABELS.RELEASED,
    iconWrap: "bg-green-100 text-green-700",
    Icon: PERIOD_STATUS_ICON.RELEASED,
  },
  BLOCKED: {
    label: PERIOD_STATUS_LABELS.BLOCKED,
    iconWrap: "bg-red-100 text-red-700",
    Icon: PERIOD_STATUS_ICON.BLOCKED,
  },
  CLOSED: {
    label: PERIOD_STATUS_LABELS.CLOSED,
    iconWrap: "bg-blue-100 text-blue-700",
    Icon: PERIOD_STATUS_ICON.CLOSED,
  },
};

interface HistoryRowProps {
  item: TaxClosingPeriodItem;
}

export function HistoryRow({ item }: HistoryRowProps) {
  const monthLabel = `${MONTHS_PT[item.month - 1]}/${item.year}`;
  const visual = STATUS_VISUAL[item.status];
  const Icon = visual.Icon;

  const when = item.releasedAt
    ? format(parseISO(item.releasedAt), "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR,
      })
    : "—";

  const releasedDisplay = item.releasedByName ?? item.releasedBy;

  return (
    <div className="flex items-center justify-between p-3 bg-muted/40 rounded-md">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "h-8 w-8 rounded-full flex items-center justify-center",
            visual.iconWrap,
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-semibold">
            {monthLabel} {visual.label}
          </p>
          <p className="text-xs text-muted-foreground">
            {releasedDisplay ? `Por ${releasedDisplay} • ` : ""}
            {when}
          </p>
        </div>
      </div>
    </div>
  );
}
